import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { CognitoUser } from "amazon-cognito-identity-js";
import React, { useState } from "react";
import { respondToTotpChallenge } from "./auth";

interface MfaVerifyProps {
  cognitoUser: CognitoUser;
  email: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const MfaVerify: React.FC<MfaVerifyProps> = ({
  cognitoUser,
  email,
  onSuccess,
  onCancel,
}) => {
  const [totpCode, setTotpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await respondToTotpChallenge(cognitoUser, totpCode);
      onSuccess();
    } catch (err: any) {
      console.error("MFA verification error:", err);
      const errorMessage = err.message || "Invalid code. Please try again.";
      setError(errorMessage);
      setTotpCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: "100%",
          mx: 2,
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Two-Factor Authentication
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3 }}
          align="center"
        >
          Enter the 6-digit code from your authenticator app for{" "}
          <strong>{email}</strong>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Authentication Code"
            type="text"
            inputMode="numeric"
            fullWidth
            value={totpCode}
            onChange={(e) => {
              // Only allow digits, max 6
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              setTotpCode(value);
            }}
            disabled={loading}
            sx={{ mb: 3 }}
            autoFocus
            autoComplete="one-time-code"
            placeholder="000000"
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading || totpCode.length !== 6}
            sx={{ mb: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : "Verify"}
          </Button>

          <Button
            variant="text"
            fullWidth
            onClick={onCancel}
            disabled={loading}
          >
            Back to Login
          </Button>
        </form>
      </Paper>
    </Box>
  );
};
